// DocumentLinkDialog — shared "Attach a file" dialog: upload a brand-new file
// OR link one that already exists in the Document Library. Used by the
// Golden Thread register and the CPP/PCI section evidence panel.
import { useEffect, useState } from "react";
import { X, Upload, Link2, Loader2, Search, FileText } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

export default function DocumentLinkDialog({ open, onClose, clientId, title = "Attach a file", onUpload, onLink }) {
    const [tab, setTab] = useState("upload");
    const [file, setFile] = useState(null);
    const [label, setLabel] = useState("");
    const [busy, setBusy] = useState(false);
    const [q, setQ] = useState("");
    const [results, setResults] = useState([]);
    const [searching, setSearching] = useState(false);
    const [selected, setSelected] = useState(null);

    useEffect(() => {
        if (!open) { setTab("upload"); setFile(null); setLabel(""); setQ(""); setResults([]); setSelected(null); }
    }, [open]);

    useEffect(() => {
        if (tab !== "link" || !clientId) return;
        setSearching(true);
        const t = setTimeout(() => {
            api.get("/documents", { params: { client_id: clientId, search: q || undefined } })
                .then((r) => setResults(r.data.slice(0, 25)))
                .catch(() => setResults([]))
                .finally(() => setSearching(false));
        }, 300);
        return () => clearTimeout(t);
    }, [tab, q, clientId]);

    if (!open) return null;

    const doUpload = async () => {
        if (!file) { toast.error("Choose a file first"); return; }
        setBusy(true);
        try { await onUpload(file, label); onClose?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Upload failed"); }
        finally { setBusy(false); }
    };

    const doLink = async () => {
        if (!selected) { toast.error("Pick a document first"); return; }
        setBusy(true);
        try { await onLink(selected.id); onClose?.(); }
        catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Link failed"); }
        finally { setBusy(false); }
    };

    return (
        <div className="fixed inset-0 z-[60] grid place-items-center p-4 animate-fade-in" style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="doc-link-dialog" className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[520px] max-h-[88vh] flex flex-col border" style={{ borderColor: "#E5E0DA" }}>
                <div className="px-5 pt-4 pb-3 border-b flex items-center justify-between" style={{ borderColor: "#F0EDE8" }}>
                    <h3 className="font-display text-[16px] font-bold text-[#1F1F1F]">{title}</h3>
                    <button data-testid="doc-link-dialog-close" onClick={onClose} className="w-8 h-8 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center"><X className="w-4 h-4" /></button>
                </div>
                <div className="flex border-b" style={{ borderColor: "#F0EDE8" }}>
                    <button data-testid="doc-link-tab-upload" onClick={() => setTab("upload")}
                        className="flex-1 py-2.5 text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5"
                        style={{ color: tab === "upload" ? "var(--brand-accent,#C75A1B)" : "#9A968F", borderBottom: tab === "upload" ? "2px solid var(--brand-accent,#C75A1B)" : "2px solid transparent" }}>
                        <Upload className="w-3.5 h-3.5" /> Upload new
                    </button>
                    <button data-testid="doc-link-tab-existing" onClick={() => setTab("link")}
                        className="flex-1 py-2.5 text-[12.5px] font-semibold inline-flex items-center justify-center gap-1.5"
                        style={{ color: tab === "link" ? "var(--brand-accent,#C75A1B)" : "#9A968F", borderBottom: tab === "link" ? "2px solid var(--brand-accent,#C75A1B)" : "2px solid transparent" }}>
                        <Link2 className="w-3.5 h-3.5" /> Link existing
                    </button>
                </div>
                <div className="px-5 py-4 flex-1 overflow-y-auto space-y-3">
                    {tab === "upload" ? (
                        <>
                            <label className="block">
                                <span className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide mb-1 block">File</span>
                                <input data-testid="doc-link-file-input" type="file" onChange={(e) => setFile(e.target.files?.[0] || null)}
                                    className="w-full text-[12.5px]" />
                            </label>
                            <label className="block">
                                <span className="text-[11px] font-semibold text-[#6F6A64] uppercase tracking-wide mb-1 block">Label (optional)</span>
                                <input data-testid="doc-link-label-input" value={label} onChange={(e) => setLabel(e.target.value)}
                                    className="w-full h-9 px-3 rounded-lg border border-[#E5E0DA] bg-white text-[12.5px]" placeholder="e.g. As-built M&E drawings — Block C" />
                            </label>
                        </>
                    ) : (
                        <>
                            <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-2" style={{ borderColor: "#E5E0DA" }}>
                                <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                                <input data-testid="doc-link-search-input" value={q} onChange={(e) => setQ(e.target.value)}
                                    placeholder="Search the Document Library…" className="flex-1 bg-transparent outline-none text-[12.5px]" />
                                {searching && <Loader2 className="w-3.5 h-3.5 animate-spin" style={{ color: "#9A968F" }} />}
                            </div>
                            <div className="space-y-1 max-h-[280px] overflow-y-auto">
                                {results.length === 0 && !searching && (
                                    <p className="text-[12px] text-[#9A968F] py-4 text-center italic">No documents found.</p>
                                )}
                                {results.map((d) => (
                                    <button key={d.id} data-testid={`doc-link-result-${d.id}`} onClick={() => setSelected(d)}
                                        className="w-full text-left px-3 py-2 rounded-lg border flex items-center gap-2.5 text-[12.5px]"
                                        style={{ borderColor: selected?.id === d.id ? "var(--brand-accent,#C75A1B)" : "#E5E0DA", background: selected?.id === d.id ? "#FBEFE5" : "#fff" }}>
                                        <FileText className="w-3.5 h-3.5 flex-shrink-0" style={{ color: "#9A968F" }} />
                                        <div className="min-w-0">
                                            <div className="font-semibold text-[#1F1F1F] truncate">{d.title}</div>
                                            <div className="text-[10.5px] text-[#9A968F] truncate">{d.category} · {d.filename}</div>
                                        </div>
                                    </button>
                                ))}
                            </div>
                        </>
                    )}
                </div>
                <div className="px-5 py-3.5 border-t flex items-center justify-end gap-2.5" style={{ borderColor: "#F0EDE8" }}>
                    <button onClick={onClose} disabled={busy} data-testid="doc-link-cancel" className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white" style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    {tab === "upload" ? (
                        <button onClick={doUpload} disabled={busy} data-testid="doc-link-upload-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />} Upload &amp; attach
                        </button>
                    ) : (
                        <button onClick={doLink} disabled={busy || !selected} data-testid="doc-link-existing-save"
                            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white disabled:opacity-50" style={{ background: "var(--brand-accent,#C75A1B)" }}>
                            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Link2 className="w-3.5 h-3.5" />} Link this document
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
}
